import { useCallback, useEffect, useState } from 'react';

import { KAKAO_SHARE_KEY } from '../../../shared/configs/config';

import { useAppSelector } from '../../../shared/store';

import { getSharedProduct, SharedProduct } from '../../service/product.service';
import { SNSType } from '../types';

import Alert from '../../../shared/components/Alert/Alert';
import { amplitudeService, ScreenName } from '../../../shared/amplitude';
import {
  ShareProduct,
} from '../../../shared/amplitude/events/product/ShareProduct';
import { branchService } from '../../../shared/branch';
import { Share } from '../../../shared/branch/events';
import { SNS_TYPE_NAME } from '../../../shared/constant/sns';
import { getFusionQueryId } from '../shared/utils/productDetailEvent';

export default function useShareProduct(productNo: number) {
  const { accessToken } = useAppSelector(({ auth }) => auth);
  const { queryId } = useAppSelector(({ productList }) => productList);
  const productDetailState = useAppSelector(({ productDetail }) => productDetail);

  const { no, name } = productDetailState;

  const [shareData, setShareData] = useState<SharedProduct>({
    no: 0,
    title: '',
    description: '',
    horizontalImageUrl: '',
    verticalImageUrl: '',
    imageWidth: 0,
    imageHeight: 0,
    productUrl: '',
    buttonTitle: '',
  });

  const getSharedProductData = useCallback(async () => {
    const data = await getSharedProduct({ productNo });
    setShareData(data);
  }, [productNo]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    amplitudeService.setScreenName(ScreenName.SHARE);

    getSharedProductData();
  }, [accessToken, getSharedProductData, productNo]);

  const handleClickShareKakao = () => {
    const {
      title, productUrl, horizontalImageUrl, imageWidth, imageHeight, buttonTitle,
    } = shareData;
    const kakao = window.Kakao;

    if (!kakao) {
      Alert({ text: '현재 카카오톡 공유하기를 사용할 수 없습니다.' });
      return;
    }

    if (!kakao.isInitialized()) {
      kakao.init(KAKAO_SHARE_KEY);
    }

    branchService.logEvent(
      new Share({
        description: 'PRODUCT',
      }, [{
        $canonical_identifier: `product/${no}`,
        $sku: no,
        $product_name: name,
      }]),
    );
    amplitudeService.logEvent(new ShareProduct({
      productDetailState,
      message: shareData.title + ', 마켓컬리에서 확인해보세요!',
      channel: '카카오톡',
      fusionQueryId: getFusionQueryId(queryId),
    }));

    kakao.Link.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        imageUrl: horizontalImageUrl,
        imageWidth: imageWidth,
        imageHeight: imageHeight,
        link: {
          mobileWebUrl: productUrl,
          webUrl: productUrl,
        },
      },
      buttons: [
        {
          title: buttonTitle,
          link: {
            mobileWebUrl: productUrl,
            webUrl: productUrl,
          },
        },
      ],
      installTalk: true,
    });
  };

  const getShareProductItem = (sns: SNSType) => {
    const { title, productUrl } = shareData;

    const encodedContentTitle = encodeURIComponent(title + ', 마켓컬리에서 확인해보세요!');
    const encodedUrl = encodeURIComponent(productUrl);

    switch (sns) {
      case 'line':
        return `https://social-plugins.line.me/lineit/share?url=${encodedUrl}&text=${encodedContentTitle}`;
      case 'facebook':
        return `http://www.facebook.com/sharer.php?u=${encodedUrl}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedContentTitle}`;
      default:
        return '';
    }
  };

  const handleClickSNSButton = (sns: SNSType) => {
    branchService.logEvent(
      new Share({
        description: 'PRODUCT',
      }, [{
        $canonical_identifier: `product/${no}`,
        $sku: no,
        $product_name: name,
      }]),
    );
    amplitudeService.logEvent(new ShareProduct({
      productDetailState,
      message: shareData.title + ', 마켓컬리에서 확인해보세요!',
      channel: SNS_TYPE_NAME[sns],
      fusionQueryId: getFusionQueryId(queryId),
    }));

    const redirectUrl = getShareProductItem(sns);
    window.open(redirectUrl, '_blank');
  };

  return {
    shareData,
    handleClickSNSButton,
    handleClickShareKakao,
  };
}
